import { Button, Tooltip } from '@patternfly/react-core';
import * as React from 'react';

import {
  SURVEY_SECTIONS,
  Submission,
  SurveyView,
  formatScore,
  respondentHandle,
  respondentLabel,
  sectionAverage,
  summarise
} from '@app/model/Survey';
import { AnswerCell, AverageCell, QuestionLabel, ToneLegend } from '@app/Classes/SurveyParts';

export interface SurveyMatrixProps {
  survey: SurveyView;
  submissions: readonly Submission[];
  /** Opens one respondent's full submission. Wired to the column headings. */
  onSelectRespondent(submission: Submission, index: number): void;
}

/**
 * Every question against every respondent, one cell each.
 *
 * The densest of the three layouts and the reason the page exists: fifty questions and a dozen
 * students is six hundred answers, and this is the arrangement that fits them on one screen. It is
 * also the only one that shows both directions of the data at once - a row that is uniformly weak
 * is a problem with the course, a column that is uniformly weak is one unhappy student, and the two
 * call for completely different responses.
 *
 * The question column is sticky, so a class with more respondents than fit scrolls sideways under
 * fixed labels rather than becoming unreadable. A column heading opens that respondent in full.
 *
 * A plain table rather than PatternFly's: the cells are 2rem of swatch and PF's own padding, sort
 * affordances and row hover would treble the height of every row, which is the one thing this
 * layout cannot afford.
 */
export const SurveyMatrix: React.FunctionComponent<SurveyMatrixProps> = ({
  survey,
  submissions,
  onSelectRespondent
}) => {
  // Summarised once for the whole grid rather than per row: every section walks every submission,
  // and on a large class that is the difference between one pass and fifty.
  const summaries = React.useMemo(
    () =>
      new Map(
        SURVEY_SECTIONS.flatMap(section => section.questions).map(question => [
          question.field,
          summarise(question, submissions)
        ])
      ),
    [submissions]
  );

  const columns = 2 + submissions.length;

  return (
    <>
      <ToneLegend showYesNo />
      <div className="cas-survey-scroll">
        <table className="cas-survey-matrix">
          <caption className="pf-v6-screen-reader">
            Every survey question against every respondent
          </caption>
          <thead>
            <tr>
              <th scope="col" className="cas-survey-qcol">Question</th>
              <th scope="col" className="cas-survey-avgcol">
                <Tooltip content="The mean of the answers held here. Not the score above, which is the survey platform's own and is computed differently.">
                  <span>Avg</span>
                </Tooltip>
              </th>
              {submissions.map((submission, index) => (
                <th scope="col" key={submission.uuid} className="cas-survey-rcol">
                  <Tooltip content={`${respondentLabel(submission, index)} — open the full submission`}>
                    <Button
                      variant="plain"
                      className="cas-survey-rbutton"
                      onClick={() => onSelectRespondent(submission, index)}
                      aria-label={`Open the submission from ${respondentLabel(submission, index)}`}
                    >
                      {respondentHandle(index)}
                    </Button>
                  </Tooltip>
                </th>
              ))}
            </tr>
          </thead>

          {SURVEY_SECTIONS.map(section => {
            const computed = sectionAverage(section, submissions);
            const reported = section.score ? (survey[section.score] as number | undefined) : undefined;

            return (
              <tbody key={section.key}>
                <tr className="cas-survey-section">
                  <th scope="colgroup" colSpan={columns}>
                    {section.title}
                    {section.score && (
                      <Tooltip content="The survey platform's own score for this section. It is reported, not recalculated here, so it need not equal the average of the answers beside it.">
                        <span className="cas-survey-section-score">
                          reported {formatScore(reported)}
                        </span>
                      </Tooltip>
                    )}
                    {computed !== undefined && (
                      <Tooltip content="The mean of every rating in this section, over the submissions held here.">
                        <span className="cas-survey-section-score">computed {formatScore(computed)}</span>
                      </Tooltip>
                    )}
                  </th>
                </tr>

                {section.questions.map(question => (
                  <tr key={String(question.field)}>
                    <th scope="row" className="cas-survey-qcol">
                      <QuestionLabel question={question} />
                    </th>
                    <td className="cas-survey-avgcol">
                      <AverageCell summary={summaries.get(question.field)!} />
                    </td>
                    {submissions.map(submission => (
                      <td key={submission.uuid} className="cas-survey-rcol">
                        <AnswerCell question={question} submission={submission} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            );
          })}
        </table>
      </div>
    </>
  );
};
